/**
 * Locale content spreadsheet.
 *
 * The workbook is the teacher's copy of a locale: hotspot ids are already
 * filled in, and each row is one content item. Parsing only checks that the
 * file is the template shape and that ids, order, and type codes are readable.
 * Question rules (choices, caps, NPC catalogs, and so on) are decided by
 * POST /api/component/import, which writes nothing until every row passes.
 *
 * Type codes and aliases match ev_web_v2/app/services/content_import_service.py.
 */
import * as XLSX from 'xlsx';
import JSZip from 'xlsx/jszip.js';

export const CONTENT_IMPORT_MAX_ROWS = 500;

export const CONTENT_IMPORT_COLUMNS = [
  'hotspot_id',
  'hotspot_name',
  'order',
  'type',
  'title',
  'body',
  'choice_1',
  'choice_2',
  'choice_3',
  'choice_4',
  'correct',
  'score',
  'youtube_url',
  'text_before',
  'text_after',
  'marking_correct',
  'marking_exclude',
  'marking_guidelines',
  'table_columns',
  'table_rows',
  'boxes',
  'links',
  'transcript',
  'npc_name',
  'npc_info',
  'student_mission',
  'avatar_id',
  'voice_id',
  'language_id',
  'persona_id',
  'time_allowed',
];

// Cells that make a row "filled in". hotspot_id, hotspot_name, and type do not:
// the downloaded template sets those and leaves everything else blank.
const CONTENT_FIELDS = [
  'title', 'order', 'score', 'body',
  'choice_1', 'choice_2', 'choice_3', 'choice_4', 'correct',
  'youtube_url', 'text_before', 'text_after',
  'marking_correct', 'marking_exclude', 'marking_guidelines',
  'table_columns', 'table_rows', 'boxes', 'links', 'transcript',
  'npc_name', 'npc_info', 'student_mission',
  'avatar_id', 'voice_id', 'language_id', 'persona_id', 'time_allowed',
];

const TYPES = {
  photo_description: 1,
  voice: 2,
  video: 3,
  mc_text: 4,
  mc_picture: 5,
  mc_text_multi: 6,
  mc_picture_multi: 7,
  question: 8,
  fill_blank: 9,
  mind_map: 10,
  photo: 11,
  table: 12,
  record: 13,
  npc: 15,
  model_3d: 16,
  drawing: 17,
};

const ALIASES = {
  intro: 'photo_description',
  photo_shot: 'photo',
  take_photo: 'photo',
  mc: 'mc_text',
};

export const CONTENT_IMPORT_STARTER_TYPES = [
  'photo_description',
  'mc_text',
  'mc_text',
  'mc_text',
  'mc_picture',
  'photo',
];

const REQUIRED_HEADERS = ['hotspot_id', 'type'];

const GUIDE_EN = [
  ['How to use', 'Place the hotspots first, then download this file. Type on the Content sheet and import it. Images, audio, and video are uploaded afterwards in the editor. Nothing is saved until every filled row passes the checks.'],
  ['hotspot_id', 'Do not change this. It decides which hotspot receives the row.'],
  ['hotspot_name', 'Checked against the id. After renaming a hotspot, download a new template.'],
  ['type', 'Choose a code from the dropdown in the type column.'],
  ['Colors', 'A colored cell is one this row\'s type uses. Change the type and the colors follow. A white cell does not apply to that type.'],
  ['body', 'The question or description. Required for most types.'],
  ['choice_1 to choice_4', 'Text choices. Fill them from choice_1 with no gaps, and enter at least two.'],
  ['correct', '1, 2, 3, or 4. For more than one right answer, write 1,3. Leave this blank on a picture question: the first image you upload is the right answer.'],
  ['score', 'Choose Not Graded, or a whole number from 0 to 10, from the dropdown. Not Graded means the question is not marked. Leave the cell blank for an intro, a voice-over, a video, or a 3D model.'],
  ['order', 'Leave blank to keep the sheet order. If you set it on one row of a hotspot, set it on every row of that hotspot.'],
  ['youtube_url', 'A YouTube link, for a video row. Otherwise upload an MP4 after import.'],
  ['text_before, text_after', 'The words on either side of a fill-in-the-blank. One of them is required.'],
  ['marking_correct, marking_exclude, marking_guidelines', 'Optional marking scheme for a question, a fill-in, or a recording.'],
  ['table_columns, table_rows', 'Comma-separated names. 2 to 6 columns and 2 to 20 rows. A name cannot contain a comma.'],
  ['boxes, links', 'Mind map. Example boxes: Museum | ___ | 1842. ___ is a blank. Example links: 1-2,2-3.'],
  ['NPC', 'npc_name, npc_info, and student_mission are required. avatar_id, voice_id (1 male, 2 female), language_id, and persona_id are all filled or all left blank. time_allowed is 2, 5, or 10.'],
  ['Types', 'photo_description, voice, video, mc_text, mc_picture, mc_text_multi, mc_picture_multi, question, fill_blank, mind_map, photo, table, record, npc, model_3d, drawing.'],
];

const GUIDE_TC = [
  ['用法', '先在地圖放好熱點，再下載這個檔案。在 Content 工作表輸入內容，然後匯入。圖片、錄音和影片要在匯入後，於編輯器裡上傳。每一列都通過檢查之前，不會寫入任何資料。'],
  ['hotspot_id', '不要改。系統靠這個編號決定題目放進哪一個熱點。'],
  ['hotspot_name', '用來核對編號。如果改了熱點名稱，請重新下載範本。'],
  ['type', '在 type 欄的下拉清單選擇類型代碼。'],
  ['顏色', '有顏色的格子是這一列的類型要填的欄。改了 type，顏色會跟著變。白色格子不適用於該類型。'],
  ['body', '題目或描述。大部分類型都要填。'],
  ['choice_1 至 choice_4', '文字選項。由 choice_1 起連續填寫，不可留空，至少兩個。'],
  ['correct', '填 1、2、3 或 4。多於一個正確答案時寫 1,3。圖片選擇題留空：之後上傳的第一張圖就是正確答案。'],
  ['score', '在 score 欄的下拉清單選擇「不計分」，或 0 至 10。不計分代表這題不評分。圖文、聲音、影片和 3D 模型請留空。'],
  ['order', '留空就會按表格由上至下排列。如果同一個熱點有一列填了次序，該熱點的每一列都要填。'],
  ['youtube_url', '影片列可填 YouTube 連結。否則請在匯入後上傳 MP4。'],
  ['text_before、text_after', '填充題空白的前後文字，至少填一邊。'],
  ['marking_correct、marking_exclude、marking_guidelines', '問答、填充或錄音題的評分準則，可以留空。'],
  ['table_columns、table_rows', '用逗號分隔名稱。直欄 2 至 6 個，橫列 2 至 20 個。名稱本身不可含逗號。'],
  ['boxes、links', '概念圖。boxes 例子：博物館 | ___ | 1842。___ 是填空格。links 例子：1-2,2-3。'],
  ['NPC', 'npc_name、npc_info、student_mission 必填。avatar_id、voice_id（1 男、2 女）、language_id、persona_id 要全部填寫，或全部留空。time_allowed 只可填 2、5 或 10。'],
  ['類型', 'photo_description, voice, video, mc_text, mc_picture, mc_text_multi, mc_picture_multi, question, fill_blank, mind_map, photo, table, record, npc, model_3d, drawing。'],
];

export function cellText(value) {
  if (value == null) {
    return '';
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return '';
    }
    const rounded = Math.round(value);
    if (Math.abs(value - rounded) < 1e-9) {
      return String(rounded);
    }
    return String(value);
  }
  if (typeof value === 'boolean') {
    return value ? 'TRUE' : 'FALSE';
  }
  if (value instanceof Date) {
    return '';
  }
  if (typeof value === 'object') {
    if (Array.isArray(value.richText)) {
      return value.richText.map((part) => (part && part.text) || '').join('').trim();
    }
    if (typeof value.text === 'string') {
      return value.text.trim();
    }
    return '';
  }
  return String(value).replace(/^\uFEFF/, '').trim();
}

export function canonicalContentType(value) {
  const raw = cellText(value).toLowerCase().replace(/[\s-]+/g, '_');
  if (!raw) {
    return '';
  }
  const code = ALIASES[raw] || raw;
  return Object.prototype.hasOwnProperty.call(TYPES, code) ? code : null;
}

function headerKey(value) {
  return cellText(value).toLowerCase().replace(/[\s-]+/g, '_');
}

// The editor's score menu shows these labels for "no marks" (score_max -1).
// A blank cell means the same thing, so either label is cleared before a row
// is treated as filled in.
const NOT_GRADED_LABELS = new Set(['不計分', 'not graded']);

function isNotGradedScore(text) {
  const folded = String(text || '').replace(/\s+/g, ' ').trim().toLowerCase();
  return NOT_GRADED_LABELS.has(folded);
}

function parseWhole(text, min, max) {
  if (text === '') {
    return { empty: true, ok: false };
  }
  if (!/^\d+$/.test(text)) {
    return { empty: false, ok: false };
  }
  const value = Number(text);
  if (!Number.isSafeInteger(value) || value < min || value > max) {
    return { empty: false, ok: false };
  }
  return { empty: false, ok: true, value };
}

function emptyCells() {
  const cells = {};
  CONTENT_IMPORT_COLUMNS.forEach((key) => {
    cells[key] = '';
  });
  return cells;
}

function isContentBlank(cells) {
  return CONTENT_FIELDS.every((key) => !cells[key]);
}

function issue(rowNumber, code, field, detail) {
  return {
    row_number: rowNumber,
    code,
    field: field || '',
    detail: detail || {},
    message: '',
  };
}

export function formatContentImportIssue(strings, item) {
  const code = String((item && item.code) || '');
  const key = 'CONTENT_IMPORT_' + code.toUpperCase();
  const template = strings && strings[key];
  let text = template || (item && item.message) || code;
  const bag = Object.assign({ row: item && item.row_number ? item.row_number : '' }, (item && item.detail) || {});
  Object.keys(bag).forEach((name) => {
    const value = bag[name] == null ? '' : String(bag[name]);
    text = text.split('{' + name + '}').join(value);
  });
  if (!template && item && item.row_number) {
    const prefix = (strings && strings.CONTENT_IMPORT_ROW_PREFIX) || 'Row {row}: ';
    text = prefix.split('{row}').join(String(item.row_number)) + text;
  }
  return text;
}

export function contentImportTemplateFileName(localeName) {
  const base = String(localeName || 'locale')
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim() || 'locale';
  return base + '_content_template.xlsx';
}

export function buildContentImportTemplate(hotspots, language) {
  const guide = (language === 'en') ? GUIDE_EN : GUIDE_TC;
  const content = [CONTENT_IMPORT_COLUMNS.slice()];
  (hotspots || []).forEach((hotspot) => {
    const id = Number(hotspot && hotspot.id);
    if (!Number.isInteger(id) || id <= 0) {
      return;
    }
    const name = cellText(hotspot.name);
    CONTENT_IMPORT_STARTER_TYPES.forEach((type) => {
      const cells = emptyCells();
      cells.hotspot_id = id;
      cells.hotspot_name = name;
      cells.type = type;
      content.push(CONTENT_IMPORT_COLUMNS.map((key) => cells[key]));
    });
  });

  const contentSheet = XLSX.utils.aoa_to_sheet(content);
  contentSheet['!cols'] = CONTENT_IMPORT_COLUMNS.map((key) => {
    if (key === 'body' || key === 'npc_info' || key === 'student_mission') {
      return { wch: 36 };
    }
    if (key === 'hotspot_name' || key === 'type') {
      return { wch: 22 };
    }
    return { wch: 16 };
  });

  const hotspotSheet = XLSX.utils.aoa_to_sheet([
    ['hotspot_id', 'hotspot_name'],
    ...(hotspots || []).map((hotspot) => [Number(hotspot.id) || '', cellText(hotspot.name)]),
  ]);
  hotspotSheet['!cols'] = [{ wch: 14 }, { wch: 28 }];

  const guideSheet = XLSX.utils.aoa_to_sheet([['Topic', 'Note'], ...guide]);
  guideSheet['!cols'] = [{ wch: 42 }, { wch: 88 }];

  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, guideSheet, 'Guide');
  XLSX.utils.book_append_sheet(book, hotspotSheet, 'Hotspots');
  XLSX.utils.book_append_sheet(book, contentSheet, 'Content');
  return colorContentColumns(XLSX.write(book, { bookType: 'xlsx', type: 'array' }), language);
}

// Cells a type uses, besides hotspot_id, hotspot_name, and type. title and
// order are colored on every known type. score is colored only when that
// type is graded. Keep this beside TYPE_FIELDS in content_import_service.py.
const TYPE_INPUTS = {
  photo_description: ['body'],
  voice: ['body', 'transcript'],
  video: ['body', 'youtube_url'],
  mc_text: ['body', 'choice_1', 'choice_2', 'choice_3', 'choice_4', 'correct'],
  mc_picture: ['body'],
  mc_text_multi: ['body', 'choice_1', 'choice_2', 'choice_3', 'choice_4', 'correct'],
  mc_picture_multi: ['body', 'correct'],
  question: ['body', 'marking_correct', 'marking_exclude', 'marking_guidelines'],
  fill_blank: ['body', 'text_before', 'text_after', 'marking_correct', 'marking_exclude', 'marking_guidelines'],
  mind_map: ['boxes', 'links'],
  photo: ['body'],
  table: ['table_columns', 'table_rows'],
  record: ['body', 'marking_correct', 'marking_exclude', 'marking_guidelines'],
  npc: ['npc_name', 'npc_info', 'student_mission', 'avatar_id', 'voice_id', 'language_id', 'persona_id', 'time_allowed'],
  model_3d: ['body'],
  drawing: ['body'],
};

const SCORED_TYPES = new Set([
  'mc_text', 'mc_picture', 'mc_text_multi', 'mc_picture_multi',
  'question', 'fill_blank', 'mind_map', 'photo', 'table', 'record', 'npc', 'drawing',
]);

const TYPE_ALIASES = {
  photo_description: ['photo_description', 'intro'],
  photo: ['photo', 'photo_shot', 'take_photo'],
  mc_text: ['mc_text', 'mc'],
};

// Light fills, one per type, plus a neutral for title and order.
const SHARED_FILL = 'FFF4F4F4';
const TYPE_FILLS = {
  photo_description: 'FFF8CBAD',
  voice: 'FFE2D5F1',
  video: 'FFBDD7EE',
  mc_text: 'FFC6EFCE',
  mc_picture: 'FFFFF2CC',
  mc_text_multi: 'FFC9DAF8',
  mc_picture_multi: 'FFFCE4D6',
  question: 'FFF4CCCC',
  fill_blank: 'FFD9EAD3',
  mind_map: 'FFEAD1DC',
  photo: 'FFD0E0E3',
  table: 'FFD9D2E9',
  record: 'FFD5A6BD',
  npc: 'FFF9CB9C',
  model_3d: 'FFB4A7D6',
  drawing: 'FFB6D7A8',
};

function columnLetter(index) {
  let n = index + 1;
  let letters = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    letters = String.fromCharCode(65 + rem) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters;
}

function typeFormula(typeCode, typeColumn) {
  const names = TYPE_ALIASES[typeCode] || [typeCode];
  const checks = names.map((name) => '$' + typeColumn + '2="' + name + '"');
  if (checks.length === 1) {
    return checks[0];
  }
  return 'OR(' + checks.join(',') + ')';
}

function fillDxf(rgb) {
  return '<dxf><fill><patternFill patternType="solid"><fgColor rgb="' + rgb + '"/><bgColor rgb="' + rgb + '"/></patternFill></fill></dxf>';
}

function xmlText(value) {
  return String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function listValidation(sqref, list) {
  return '<dataValidation type="list" allowBlank="1" showErrorMessage="1" showInputMessage="0" sqref="' + sqref + '"><formula1>"' + xmlText(list) + '"</formula1></dataValidation>';
}

/**
 * Color the input cells of each content type.
 * The spreadsheet library writes values and skips fills, so this adds Excel
 * conditional formatting: the color follows the type cell, including a type
 * the teacher types after downloading.
 */
function colorContentColumns(bytes, language) {
  const zip = new JSZip(bytes);
  const workbook = zip.file('xl/workbook.xml').asText();
  const rels = zip.file('xl/_rels/workbook.xml.rels').asText();
  const sheetTag = (workbook.match(/<sheet\b[^>]*>/g) || []).find((tag) => /name="Content"/.test(tag));
  const relationId = sheetTag && (sheetTag.match(/\br:id="([^"]+)"/) || [])[1];
  const relation = relationId && rels.match(new RegExp('<Relationship\\b[^>]*Id="' + relationId + '"[^>]*>'));
  const target = relation && (relation[0].match(/\bTarget="([^"]+)"/) || [])[1];
  if (!target) {
    return bytes;
  }
  const sheetPath = target.charAt(0) === '/' ? target.slice(1) : 'xl/' + target.replace(/^\/?xl\//, '');

  const indexByKey = {};
  CONTENT_IMPORT_COLUMNS.forEach((key, index) => {
    indexByKey[key] = index;
  });
  const typeColumn = columnLetter(indexByKey.type);
  const lastRow = CONTENT_IMPORT_MAX_ROWS + 1;
  const fills = [SHARED_FILL].concat(Object.keys(TYPE_FILLS).map((code) => TYPE_FILLS[code]));
  const dxfByType = {};
  Object.keys(TYPE_FILLS).forEach((code, index) => {
    dxfByType[code] = index + 1;
  });

  const rulesByLetter = {};
  function addRule(key, dxfId, formula) {
    const letter = columnLetter(indexByKey[key]);
    if (!rulesByLetter[letter]) {
      rulesByLetter[letter] = [];
    }
    rulesByLetter[letter].push({ dxfId, formula });
  }

  const knownNames = [];
  Object.keys(TYPE_INPUTS).forEach((code) => {
    (TYPE_ALIASES[code] || [code]).forEach((name) => {
      if (knownNames.indexOf(name) === -1) {
        knownNames.push(name);
      }
    });
    const formula = typeFormula(code, typeColumn);
    TYPE_INPUTS[code].forEach((key) => addRule(key, dxfByType[code], formula));
    if (SCORED_TYPES.has(code)) {
      addRule('score', dxfByType[code], formula);
    }
  });
  const sharedFormula = 'OR(' + knownNames.map((name) => '$' + typeColumn + '2="' + name + '"').join(',') + ')';
  ['title', 'order'].forEach((key) => addRule(key, 0, sharedFormula));

  let priority = 1;
  const blocks = Object.keys(rulesByLetter).map((letter) => {
    const rules = rulesByLetter[letter].map((rule) => {
      const xml = '<cfRule type="expression" dxfId="' + rule.dxfId + '" priority="' + priority + '"><formula>' + rule.formula + '</formula></cfRule>';
      priority += 1;
      return xml;
    }).join('');
    return '<conditionalFormatting sqref="' + letter + '2:' + letter + lastRow + '">' + rules + '</conditionalFormatting>';
  }).join('');

  const typeList = Object.keys(TYPE_INPUTS).join(',');
  const scoreLabel = language === 'en' ? 'Not Graded' : '不計分';
  const scoreList = scoreLabel + ',0,1,2,3,4,5,6,7,8,9,10';
  const scoreColumn = columnLetter(indexByKey.score);
  const validations = '<dataValidations count="2">'
    + listValidation(typeColumn + '2:' + typeColumn + lastRow, typeList)
    + listValidation(scoreColumn + '2:' + scoreColumn + lastRow, scoreList)
    + '</dataValidations>';

  // Colors and dropdowns have to sit immediately after the cells, dropdowns
  // after the colors. Excel drops the whole sheet when either one follows
  // ignoredErrors.
  const sheetXml = zip.file(sheetPath).asText().replace(
    '</sheetData>',
    '</sheetData>' + blocks + validations,
  );
  zip.file(sheetPath, sheetXml);

  const styles = zip.file('xl/styles.xml').asText().replace(
    /<dxfs count="0"\s*\/>/,
    '<dxfs count="' + fills.length + '">' + fills.map(fillDxf).join('') + '</dxfs>',
  );
  zip.file('xl/styles.xml', styles);

  return zip.generate({ type: 'uint8array', compression: 'DEFLATE' });
}

/**
 * Read a workbook into request rows.
 * Returns { error, localIssues, requestRows, skipped }.
 * `error` is a file-level problem. `localIssues` are rows the server should
 * not be asked to judge, because the cell is not a number or not a type code.
 */
export function parseContentImportWorkbook(arrayBuffer) {
  let book;
  try {
    book = XLSX.read(arrayBuffer, { type: 'array' });
  } catch (e) {
    return { error: issue(0, 'unreadable_file'), localIssues: [], requestRows: [], skipped: 0 };
  }
  const sheetName = (book.SheetNames || []).find((name) => String(name).trim().toLowerCase() === 'content');
  if (!sheetName) {
    return { error: issue(0, 'no_content_sheet'), localIssues: [], requestRows: [], skipped: 0 };
  }
  const table = XLSX.utils.sheet_to_json(book.Sheets[sheetName], {
    header: 1,
    raw: true,
    defval: '',
  });
  if (!table.length) {
    return { error: issue(0, 'empty_file'), localIssues: [], requestRows: [], skipped: 0 };
  }

  const header = (table[0] || []).map(headerKey).filter((key) => key);
  const missing = REQUIRED_HEADERS.filter((key) => !header.includes(key));
  const known = new Set(CONTENT_IMPORT_COLUMNS);
  const unexpected = header.filter((key) => !known.has(key));
  if (missing.length || unexpected.length) {
    return {
      error: issue(0, 'bad_headers', '', {
        missing: missing.join(', ') || '-',
        unexpected: unexpected.join(', ') || '-',
      }),
      localIssues: [],
      requestRows: [],
      skipped: 0,
    };
  }

  const indexByKey = {};
  header.forEach((key, index) => {
    if (indexByKey[key] == null) {
      indexByKey[key] = index;
    }
  });

  const localIssues = [];
  const requestRows = [];
  let skipped = 0;
  let used = 0;
  for (let i = 1; i < table.length; i++) {
    const source = table[i] || [];
    const cells = emptyCells();
    let any = false;
    CONTENT_IMPORT_COLUMNS.forEach((key) => {
      if (indexByKey[key] == null) {
        return;
      }
      const text = cellText(source[indexByKey[key]]);
      cells[key] = key === 'score' && isNotGradedScore(text) ? '' : text;
      if (text) {
        any = true;
      }
    });
    if (!any) {
      continue;
    }
    used += 1;
    if (used > CONTENT_IMPORT_MAX_ROWS) {
      return {
        error: issue(0, 'too_many_rows', '', { max: CONTENT_IMPORT_MAX_ROWS }),
        localIssues: [],
        requestRows: [],
        skipped: 0,
      };
    }
    const excelRow = i + 1;
    const contentBlank = isContentBlank(cells);
    const id = parseWhole(cells.hotspot_id, 1, 2147483647);
    const order = parseWhole(cells.order, 1, 10000);
    const typeCode = canonicalContentType(cells.type);
    if (!id.empty && !id.ok) {
      localIssues.push(issue(excelRow, 'hotspot_invalid', 'hotspot_id', { value: cells.hotspot_id }));
    }
    if (!order.empty && !order.ok) {
      localIssues.push(issue(excelRow, 'order_invalid', 'order', { value: cells.order }));
    }
    if (typeCode == null) {
      localIssues.push(issue(excelRow, 'type_unknown', 'type', { value: cells.type }));
    }
    if (contentBlank) {
      skipped += 1;
      continue;
    }
    const request = { row_number: excelRow };
    CONTENT_IMPORT_COLUMNS.forEach((key) => {
      if (key === 'hotspot_id' || key === 'order') {
        return;
      }
      request[key] = cells[key];
    });
    request.hotspot_id = id.ok ? id.value : null;
    request.order = order.ok ? order.value : null;
    requestRows.push(request);
  }

  if (!localIssues.length && !requestRows.length) {
    return { error: issue(0, 'nothing_to_import'), localIssues: [], requestRows: [], skipped };
  }
  return { error: null, localIssues, requestRows, skipped };
}
